import { useState } from 'react';
import type { RecurringDonorDto } from '@signalvote/shared';
import {
  useRecurringDonors,
  useCancelRecurringDonor,
} from '@/hooks/queries/useRecurringCutoff';
import { formatAmount, fmtDateLong } from '@/components/finance/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/card';

/**
 * The per-donor recurring-donors list on the Donations page — who is giving recurringly (donor / amount /
 * cadence / next charge / status), with a per-row cancel. These are DONOR recurring gifts on the
 * committee's connected account (not the campaign's own SaaS subscription). Read is Finance/View; the
 * cancel is Finance/Manage, enforced by the API. Sits alongside the recent-donations feed.
 */
export function RecurringDonors() {
  const { data, isLoading, isError, refetch } = useRecurringDonors();
  const donors = data ?? [];

  return (
    <Card data-testid="recurring-donors">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Recurring donors
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2" data-testid="recurring-donors-skeleton">
            <div className="h-6 rounded bg-muted/40 animate-pulse" />
            <div className="h-6 rounded bg-muted/40 animate-pulse" />
          </div>
        ) : isError ? (
          <div className="text-sm text-muted-foreground">
            Couldn&apos;t load recurring donors.{' '}
            <button
              type="button"
              onClick={() => void refetch()}
              className="font-medium text-primary underline underline-offset-2"
              data-testid="recurring-donors-retry"
            >
              Retry
            </button>
          </div>
        ) : donors.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No recurring donors yet. Monthly gifts set up on your donation page appear here.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {donors.map((donor) => (
              <RecurringDonorRow key={donor.id} donor={donor} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function RecurringDonorRow({ donor }: { donor: RecurringDonorDto }) {
  const cancel = useCancelRecurringDonor();
  const [confirming, setConfirming] = useState(false);

  const isActive = donor.status === 'Active';
  // Active series: cadence (data-driven, not a hardcoded literal) + when the next charge lands.
  // Ended/canceled: show the terminal status.
  const subline = isActive
    ? donor.nextChargeAt
      ? `${donor.cadence} · next charge ${fmtDateLong(donor.nextChargeAt)}`
      : `${donor.cadence} · started ${fmtDateLong(donor.startedAt)}`
    : donor.status === 'Ended'
      ? 'Recurring ended'
      : 'Recurring canceled';

  return (
    <li
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-2"
      data-testid="recurring-donor-row"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">
          {donor.donorName ?? 'Unnamed donor'}
        </p>
        <p className="text-xs text-muted-foreground">{subline}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-foreground">
          {formatAmount(donor.amount)}/mo
        </span>
        {isActive ? (
          confirming ? (
            <div className="flex items-center gap-2" data-testid="recurring-cancel-confirm">
              <span className="text-xs text-muted-foreground">Cancel this gift?</span>
              <Button
                variant="destructive"
                size="sm"
                onClick={() =>
                  cancel.mutate(donor.id, { onSettled: () => setConfirming(false) })
                }
                disabled={cancel.isPending}
              >
                {cancel.isPending ? 'Canceling…' : 'Yes, cancel'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
                Keep
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirming(true)}
              data-testid="recurring-cancel"
            >
              Cancel
            </Button>
          )
        ) : (
          <Badge variant="outline" data-testid="recurring-donor-status">
            {donor.status}
          </Badge>
        )}
      </div>
    </li>
  );
}
